import ApartmentOutlinedIcon from "@mui/icons-material/ApartmentOutlined";
import ArrowForwardRoundedIcon from "@mui/icons-material/ArrowForwardRounded";
import PersonOutlineRoundedIcon from "@mui/icons-material/PersonOutlineRounded";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import ButtonBase from "@mui/material/ButtonBase";
import CircularProgress from "@mui/material/CircularProgress";
import Divider from "@mui/material/Divider";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { Link as RouterLink, useNavigate } from "react-router-dom";
import { useAuth } from "@bw/shared/hooks";
import { useSelectedOrganization } from "@/app/OrganizationContext";
import { clearPersistedFacilitySelection } from "@/app/FacilityContext";
import { listMyOrgOptions } from "@/libs/onboardingApi";
import { playerAppOrigin } from "@/libs/playerAppOrigin";
import { listMyStaffInvitations } from "@/libs/staffApi";
import { AccountNameEditor } from "@/components/AccountNameEditor";

const rowStyle = {
  width: "100%", minHeight: 76, p: 2, gap: 2, borderRadius: 1,
  justifyContent: "flex-start", textAlign: "left", border: 1,
  borderColor: "divider", "&:hover": { bgcolor: "action.hover" },
  "&.Mui-focusVisible": { outline: "2px solid", outlineColor: "primary.main", outlineOffset: 2 },
} as const;

/** Navigation choice only. The selected workspace never grants organization access. */
export function WorkspaceChooser() {
  const { user, signOut } = useAuth();
  const accountReady = Boolean(user?.displayName?.trim());
  const [signOutError, setSignOutError] = useState(false);
  const [signingOut, setSigningOut] = useState(false);
  const navigate = useNavigate();
  const { selectOrganization } = useSelectedOrganization();
  const query = useQuery({
    queryKey: ["org", "me", "options", user?.uid],
    queryFn: listMyOrgOptions,
    enabled: Boolean(user) && accountReady,
    retry: false,
  });
  const invitations = useQuery({
    queryKey: ["staff", "my-invitations", user?.uid],
    queryFn: listMyStaffInvitations,
    enabled: Boolean(user) && accountReady,
    retry: false,
  });
  const choose = (orgId: string) => {
    clearPersistedFacilitySelection();
    selectOrganization(orgId);
    navigate("/", { replace: true });
  };
  if (user?.email && !accountReady) {
    return <Stack component="main" spacing={3} sx={{ maxWidth: 640, mx: "auto", px: 2, py: { xs: 2, md: 5 } }}>
      <Stack direction="row" sx={{ alignItems: "center", justifyContent: "space-between", gap: 2 }}>
        <Typography color="text.secondary" variant="body2">Badminton Warriors</Typography>
        <Button disabled={signingOut} onClick={async () => {
          setSigningOut(true);
          setSignOutError(false);
          try { await signOut(); } catch { setSignOutError(true); } finally { setSigningOut(false); }
        }}>{signingOut ? "Signing out…" : "Sign out"}</Button>
      </Stack>
      {signOutError ? <Alert severity="error">We couldn’t sign you out. Please try again.</Alert> : null}
      <Stack spacing={1}>
        <Typography component="h1" variant="h4">Complete your account name</Typography>
        <Typography color="text.secondary">
          Add the name used for your BW sign-in before reviewing organization invitations.
        </Typography>
      </Stack>
      <AccountNameEditor />
    </Stack>;
  }
  return <Stack component="main" spacing={3} sx={{ maxWidth: 640, mx: "auto", py: { xs: 2, md: 5 } }}>
    <Stack direction="row" sx={{ alignItems: "center", justifyContent: "space-between", gap: 2 }}>
      <Typography color="text.secondary" variant="body2">Badminton Warriors</Typography>
      <Button disabled={signingOut} onClick={async () => {
        setSigningOut(true);
        setSignOutError(false);
        try { await signOut(); } catch { setSignOutError(true); } finally { setSigningOut(false); }
      }}>{signingOut ? "Signing out…" : "Sign out"}</Button>
    </Stack>
    {signOutError ? <Alert severity="error">We couldn’t sign you out. Please try again.</Alert> : null}
    <Stack spacing={1}>
      <Typography component="h1" variant="h4">Where would you like to go?</Typography>
      <Typography color="text.secondary">Your personal space and the organizations you belong to.</Typography>
    </Stack>
    <ButtonBase component="a" href={playerAppOrigin()} sx={rowStyle}>
      <PersonOutlineRoundedIcon color="primary" aria-hidden="true" />
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography component="span" sx={{ display: "block", fontWeight: 650 }}>Personal</Typography>
        <Typography component="span" variant="body2" color="text.secondary" sx={{ display: "block", mt: 0.25 }}>Play, bookings, and your profile</Typography>
      </Box>
      <ArrowForwardRoundedIcon fontSize="small" aria-hidden="true" />
    </ButtonBase>
    <Divider />
    {invitations.isError ? <Alert severity="warning" action={<Button onClick={() => void invitations.refetch()}>Retry</Button>}>We couldn’t check for invitations.</Alert> : null}
    {invitations.data?.some(invitation => invitation.status === "pending") ? <Stack spacing={1.5}>
      <Typography component="h2" variant="h6">Invitations</Typography>
      {invitations.data.filter(invitation => invitation.status === "pending").map(invitation => <ButtonBase
        component={RouterLink}
        to={`/staff/invitations/${encodeURIComponent(invitation.invitation_id)}`}
        key={invitation.invitation_id}
        sx={rowStyle}
      >
        <ApartmentOutlinedIcon color="primary" aria-hidden="true" />
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography sx={{ fontWeight: 650, overflowWrap: "anywhere" }}>{invitation.organization_name}</Typography>
          <Typography variant="body2" color="text.secondary">Review invitation</Typography>
        </Box>
        <ArrowForwardRoundedIcon fontSize="small" aria-hidden="true" />
      </ButtonBase>)}
    </Stack> : null}
    <Typography component="h2" variant="h6">Organizations</Typography>
    {query.isPending ? <Stack role="status" direction="row" spacing={1.5} sx={{ alignItems: "center" }}>
      <CircularProgress size={20} /><Typography>Loading your organizations…</Typography>
    </Stack> : query.isError ? <Alert severity="error" action={<Button onClick={() => void query.refetch()}>Retry</Button>}>
      We couldn’t load your organizations. Your personal space is still available.
    </Alert> : query.data?.length ? <Stack spacing={1.5}>
      {query.data.map((org) => <ButtonBase key={org.org_id} onClick={() => choose(org.org_id)} sx={rowStyle}>
        <ApartmentOutlinedIcon color="action" aria-hidden="true" />
        <Typography sx={{ flex: 1, minWidth: 0, overflowWrap: "anywhere", fontWeight: 650 }}>{org.business_name}</Typography>
        <ArrowForwardRoundedIcon fontSize="small" aria-hidden="true" />
      </ButtonBase>)}
    </Stack> : <Stack spacing={2}>
      <Typography color="text.secondary">No organizations yet. Accept an invitation from your organization to join its team.</Typography>
      <Button component={RouterLink} to="/getting-started" variant="outlined" sx={{ alignSelf: "flex-start" }}>Set up an organization</Button>
    </Stack>}
  </Stack>;
}
